import { Component, OnInit, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FractionationService } from 'src/app/shared/services/fractionations.service';
import { FormBuilder, FormGroup, FormArray, FormControl, AbstractControl, Validators } from '@angular/forms';
import { Carrier, CarrierService } from 'src/app/shared/services/carrier.service';
import { ProviderAccountService } from 'src/app/shared/services/provider-account.service';
import { ProviderAccount } from 'src/app/models/ProviderAccount';
import { FractionGroupProvider, FractionGroupProviderService } from 'src/app/shared/services/fractionation-provider-pivot.service';
import { ToastService } from 'src/app/shared/components/toast/toast.service';
import { FractionationChannel, FractionationPrincipalRoutes, FractionationRoute } from 'src/app/models/Fractionation';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmationService } from 'src/app/shared/components/confirmation-modal/confirmation.service';
import { firstValueFrom } from 'rxjs';
import { AccountVersionService } from 'src/app/shared/services/account-version.service';
import { SearchSelectOption } from 'src/app/shared/components/search-select/search-select.component';

/**
 * Detalhamento de rota de Fracionamentos, layout v2 (CU-17tm466nzee, 14/10/2026).
 *
 * Regras que NÃO mudaram (só a apresentação — ver `fractionation.component.html`):
 * - `getRowRemainingPercentage` e a soma de `saveValues()` continuam idênticas, inclusive o
 *   efeito colateral de tratar `percentageControl` string ('70%') virando number na primeira
 *   leitura — qualquer mudança aqui quebraria a validação de soma 100%.
 * - `saveValues()` manda o MESMO payload de antes (`FractionGroupProviderService.addRow`), com
 *   os mesmos nomes de campo (`carrierControl.id`/`.title`, `providerControl.id`); o botão
 *   "Salvar" só saiu do rodapé de cada card de operadora para o cabeçalho (decisão aprovada
 *   pelo Bruno no mockup, T.12) — a chamada é a mesma.
 * - "Qualquer operadora" continua obrigatória só em `createRoute()` (criar), nunca em
 *   `saveValues()` (editar).
 * - As 3 confirmações (padrão/monitorada/pix), `createConfiguration`/`updateConfiguration` e o
 *   modal de nome (`configurationForm`) são os mesmos métodos, só com o template restilizado.
 * - Remover operadora continua com a mesma condição (`index != 0 && length > 1`).
 *
 * O que mudou (só troca de controle, não de regra):
 * - O autocomplete do Material saiu pelo `app-search-select` (CVA que só guarda `string|null`).
 *   Como `carrierControl`/`providerControl` continuam guardando o OBJETO `Carrier`/
 *   `ProviderAccount` inteiro (é o que `saveValues()`/`populateForm()` esperam), o template usa
 *   `[ngModel]` (one-way, só para mostrar a seleção atual pelo id) + `(selectionChange)` →
 *   `setCarrier`/`setProvider`, que procuram o objeto completo em `carrierOptions`/
 *   `providerAccountOptions` pelo id e gravam no FormControl de sempre. `displayCarrierFn`/
 *   `displayProviderFn`/`filteredCarrierOptions`/`filteredProviderOptions`/
 *   `loadCarrierAutoComplete`/`loadProviderAutoComplete` e o `providerControl` solto (vestígio
 *   do Material, nunca lido por ninguém) saíram por serem só plumbing do autocomplete antigo.
 */
@Component({
  selector: 'app-fractionation',
  templateUrl: './fractionation.component.html',
  styleUrls: ['./fractionation.component.scss'],
})
export class FractionationComponent implements OnInit {
  @ViewChild('modalConfiguration', {static: false}) modalConfiguration: any;
  routeData: FractionationRoute;
  carrierOptions: Carrier[] = [];
  providerAccountOptions: ProviderAccount[] = [];
  carrierSelectOptions: SearchSelectOption[] = [];
  providerSelectOptions: SearchSelectOption[] = [];
  form: FormGroup;
  remainingPercentValue: number = 100;
  defaultCarrier: Carrier;
  previousProviderValue: ProviderAccount | null = null;
  validatePercentage: boolean = false;

  carriersProvidersData: FormArray;
  routeId: string;

  /** Carga da rota; e gravação das porcentagens, que gira o botão Salvar. */
  loadingRoute = false;
  saving = false;

  result: FractionationRoute[];
  principalRoutes: FractionationPrincipalRoutes;
  confirmatedReplaceDefaultRoute: boolean = false;
  confirmatedReplaceMonitoredRoute: boolean = false;
  confirmatedReplacePixRoute: boolean = false;

  configurationForm: FormGroup;

  /**
   * Canal da rota (RCS, 17tm466frtx). Na criação vem da tab de onde o admin
   * saiu (`?channel=rcs`); na edição, da própria rota. Decide quais contas de
   * provedor aparecem (no RCS, só as liberadas pelo dev) e se a criação manda os
   * papéis de sistema: no RCS a rota nasce sem papel, e padrão/monitorada/Pix se
   * ligam depois pelos botões da lista (por canal, ver `FractionationsComponent`).
   */
  channel: FractionationChannel = 'sms';

  get isRcs(): boolean {
    return this.channel === 'rcs';
  }

  // Cores da barra de divisão (`m8v-split-bar`), na mesma ordem do mockup aprovado — tokens
  // de tema (`--sms-*`), nunca hex solto.
  private readonly splitColors = [
    'var(--sms-primary)',
    'var(--sms-accent-cool)',
    'var(--sms-accent-violet)',
    'var(--sms-accent-warm)',
    'var(--sms-danger)'
  ];

  constructor(
    private fractionationService: FractionationService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private carrierService: CarrierService,
    private providerAccount: ProviderAccountService,
    private fb: FormBuilder,
    private FractionGroupProviderService: FractionGroupProviderService,
    private toastService: ToastService,
    private modalService: NgbModal,
    private confirmationService: ConfirmationService,
    private accountVersionService: AccountVersionService
  ) {
  }

  async ngOnInit() {
    this.form = this.fb.group({
      carriers: this.fb.array([]),
    });

    this.fractionationService.getPrincipalRoutes().subscribe(res => {
      this.principalRoutes = res;
    });

    this.configurationForm = this.fb.group({
      routeName: ['', Validators.required],
      default: [false],
      monitored: [false],
      pix: [false],
    });

    this.configurationForm.get('default')?.valueChanges.subscribe((value) => {
      if (!value) return;

      const defaultRouteName = this.principalRoutes.default?.name;
      const defaultControl = this.configurationForm.get('default');

      if (defaultRouteName && !this.confirmatedReplaceDefaultRoute) {
        this.confirmationService.show(
          `Tem certeza que deseja substituir a rota "${defaultRouteName}" pela sua para o papel de <b>PADRÃO DE TODO O SISTEMA</b>?`,
          () => {
            this.confirmatedReplaceDefaultRoute = true;
            defaultControl?.setValue(true)
          },
          () => defaultControl?.setValue(false)
        );
      }
    });

    this.configurationForm.get('monitored')?.valueChanges.subscribe((value) => {
      if (!value) return;

      const monitoredRouteName = this.principalRoutes.monitored?.name;
      const monitoredControl = this.configurationForm.get('monitored');

      if (monitoredRouteName && !this.confirmatedReplaceMonitoredRoute) {
        this.confirmationService.show(
          `Tem certeza que deseja substituir a rota "${monitoredRouteName}" pela sua para o papel de <b>MONITORADA DE TODO O SISTEMA</b>?`,
          () => {
            this.confirmatedReplaceMonitoredRoute = true;
            monitoredControl?.setValue(true)
          },
          () => monitoredControl?.setValue(false)
        );
      }
    });

    this.configurationForm.get('pix')?.valueChanges.subscribe((value) => {
      if (!value) return;

      const pixRouteName = this.principalRoutes.pix?.name;
      const pixControl = this.configurationForm.get('pix');

      if (pixRouteName && !this.confirmatedReplacePixRoute) {
        this.confirmationService.show(
          `Tem certeza que deseja substituir a rota "${pixRouteName}" pela sua para o papel de <b>PIX DE TODO O SISTEMA</b>?`,
          () => {
            this.confirmatedReplacePixRoute = true;
            pixControl?.setValue(true)
          },
          () => pixControl?.setValue(false)
        );
      }
    });

    const queryChannel = this.activatedRoute.snapshot?.queryParamMap?.get('channel');
    this.channel = queryChannel === 'rcs' ? 'rcs' : 'sms';

    this.activatedRoute.params.subscribe(async params => {
      const id = params['id'];
      this.routeId = id;

      if (id && id != 'new') {
        await this.fetchCarriers();

        // As contas dependem do canal, que só se sabe com a rota carregada.
        this.fetchRoute(id);
      }
    });
  }

  ngAfterViewInit() {
    if (this.routeId == 'new') {
      this.openConfigurationModal();
    }
  }

  /**
   * O modal pendura no `<body>`, fora do `.version-international` do shell: sem
   * a classe do tema, o botão primário sairia verde na versão internacional.
   */
  private openConfigurationModal(): NgbModalRef {
    return this.modalService.open(this.modalConfiguration, {
      backdrop: 'static',
      keyboard: false,
      windowClass: this.accountVersionService.themeClass,
    });
  }


  get carriers(): FormArray {
    return this.form.get('carriers') as FormArray;
  }

  removeCarrierRow(index: number) {
    this.carriers.removeAt(index);
  }

  /**
   * Mesma condição de hoje pro botão de remover operadora (antes inline no `*ngIf` do
   * template, `carriers.controls.indexOf(carrier) != 0 && carriers.controls.length > 1`):
   * nunca a primeira operadora, e só quando há mais de uma. Extraído só por testabilidade —
   * não muda o comportamento.
   */
  canRemoveCarrier(index: number): boolean {
    return index !== 0 && this.carriers.length > 1;
  }

  addCarrierRow(carrier: any = null, percentage: number = 100): FormGroup {
    const carrierGroup = this.fb.group({
      percentageControl: new FormControl(percentage),
      carrierControl: new FormControl(carrier),
      providers: this.fb.array([])
    });

    this.carriers.push(carrierGroup);

    return carrierGroup;
  }

  getProviderArray(carrierGroup: AbstractControl): FormArray {
    return carrierGroup.get('providers') as FormArray;
  }

  removeProviderRow(group: AbstractControl, index: number) {
    this.getProviderArray(group).removeAt(index);
  }


  addProviderRow(carrierGroup: AbstractControl, provider: any = null, percentage: string = ''): void {
    const providers = this.getProviderArray(carrierGroup);

    const providerGroup = this.fb.group({
      providerControl: new FormControl(provider),
      percentageControl: new FormControl(percentage)
    });

    providers.push(providerGroup);
  }

  getProviders(carrierIndex: number): FormArray {
    return this.carriers.at(carrierIndex).get('providers') as FormArray;
  }

  /**
   * Adaptador do `app-search-select` de operadora (`[ngModel]`/`(selectionChange)` no
   * template): o select só manda/recebe `string|null` (o id), mas `carrierControl` precisa
   * continuar com o OBJETO `Carrier` inteiro — é o que `saveValues()`/`populateForm()` leem
   * (`.id`/`.title`). Acha o objeto completo em `carrierOptions` pelo id e grava no mesmo
   * FormControl de sempre; não muda nenhuma regra de cálculo/validação.
   */
  setCarrier(index: number, carrierId: string | null): void {
    const carrier = this.carrierOptions.find(c => c.id === carrierId) || null;
    (this.carriers.at(index) as FormGroup).get('carrierControl')?.setValue(carrier);
  }

  /** Mesmo adaptador de `setCarrier`, para a conta de provedor de cada saída. */
  setProvider(carrierIndex: number, providerIndex: number, providerId: string | null): void {
    const provider = this.providerAccountOptions.find(p => p.id === providerId) || null;
    (this.getProviders(carrierIndex).at(providerIndex) as FormGroup).get('providerControl')?.setValue(provider);
  }

  /** Id atual (pro `[ngModel]` do `app-search-select`), lido do objeto que já está no controle. */
  carrierValue(index: number): string | null {
    return this.carriers.at(index)?.get('carrierControl')?.value?.id ?? null;
  }

  providerValue(carrierIndex: number, providerIndex: number): string | null {
    return this.getProviders(carrierIndex)?.at(providerIndex)?.get('providerControl')?.value?.id ?? null;
  }

  fetchRoute(id: string) {
    this.loadingRoute = true;
    this.fractionationService.getRoute(id).subscribe({
      next: async (data: FractionationRoute) => {
        this.routeData = data;
        this.channel = data?.channel === 'rcs' ? 'rcs' : 'sms';
        this.configurationForm.get('routeName').setValue(this.routeData.name);

        await this.fetchProviders();
        this.loadingRoute = false;

        this.FractionGroupProviderService.getRowsByFractionationId(this.routeData.id).subscribe({
          next: (data: any) => {
            if (typeof data === 'object' && !Array.isArray(data)) {
              this.populateForm(data);
              this.carriersProvidersData = data as FormArray;
            } else {
              if (Object.keys(data).length == 0) {
                const carrier = this.carrierOptions.find(c => c.title.toLowerCase() == 'qualquer operadora');
                this.addCarrierRow(carrier, 0);
              }
              console.error('Erro: dados recebidos não estão no formato esperado:', data);
            }
          },
          error: (error) => {
            console.error('Erro ao obter os dados de fractionation provider pivot:', error);
          }
        });
      },
      error: (error) => {
        this.loadingRoute = false;
        console.error('Erro ao obter a rota de fractionation:', error);
      }
    });
  }

  populateForm(data: { [carrier_id: string]: FractionGroupProvider[] }) {
    Object.keys(data).forEach((carrierId) => {
      const carrier = this.carrierOptions.find(c => c.id == carrierId);
      if (!carrier) {
        console.error('Erro: operadora não encontrada:', carrierId);
        return;
      }
      this.addCarrierRow(carrier, 0);
      this.carriers.controls[this.carriers.length - 1].get('percentageControl').setValue('0');
      const providers = data[carrierId];

      providers.forEach((provider) => {
        const providerAccount = this.providerAccountOptions.find(p => p.id === provider.provider_account_id);
        if (providerAccount) {
          this.addProviderRow(this.carriers.controls[this.carriers.length - 1], providerAccount, provider.percentage_provider);
        } else {
          console.error('Erro: provider account não encontrado:', provider.provider_account_id);
        }
      });
    });
  }

  async fetchCarriers() {
    try {
      this.carrierOptions = await firstValueFrom(this.carrierService.listAll());
      this.carrierSelectOptions = this.carrierOptions.map(c => ({ value: c.id, label: c.title }));
    } catch (error) {
      console.error('Erro ao obter os dados de carrier:', error);
    }
  }

  async fetchProviders() {
    try {
      this.providerAccountOptions = await firstValueFrom(this.providerAccount.listAll(this.channel));
      this.providerSelectOptions = this.providerAccountOptions.map(p => ({ value: p.id, label: p.name }));
    } catch (error) {
      console.error('Erro ao obter os dados de provider account:', error);
    }
  }

  redirectBack() {
    this.router.navigate(['/fractionations'], { queryParams: { channel: this.channel } });
  }

  getRowRemainingPercentage(index: number): number {
    const totalRemainingPercentage = this.carriers.controls[index].value.providers.reduce((sum: number, provider: any) => {
      if (typeof provider.percentageControl != 'number') {
        provider.percentageControl = parseFloat(provider.percentageControl.replace('%', ''));
      }
      return sum + (parseFloat(provider.percentageControl) || 0);
    }, 0);

    return 100 - totalRemainingPercentage
  }

  /**
   * Mesmo valor de `getRowRemainingPercentage`, só que sem o efeito colateral de mutar
   * `percentageControl` — usada apenas para desenhar a largura de cada fatia da
   * `m8v-split-bar`, nunca para validar ou salvar.
   */
  providerPercentValue(providerGroup: AbstractControl): number {
    const value = providerGroup.get('percentageControl')?.value;
    if (typeof value === 'number') {
      return value;
    }
    const parsed = parseFloat(String(value ?? '').replace('%', ''));
    return isNaN(parsed) ? 0 : parsed;
  }

  splitColor(index: number): string {
    return this.splitColors[index % this.splitColors.length];
  }

  /** Soma de saídas em todas as operadoras, pro subtítulo "N operadoras · M saídas". */
  totalOutputsCount(): number {
    return this.carriers.controls.reduce((sum, carrierGroup) => sum + this.getProviderArray(carrierGroup).length, 0);
  }

  /**
   * Aviso (não bloqueia o Salvar — só `createRoute()` exige isso hoje): "Qualquer operadora"
   * sem nenhuma saída configurada.
   */
  get anyCarrierOutputsMissing(): boolean {
    const anyCarrier = this.carriers.controls.find(carrierGroup => {
      const carrier = carrierGroup.get('carrierControl')?.value;
      return !!carrier?.title && carrier.title.toLowerCase() === 'qualquer operadora';
    });
    return !anyCarrier || this.getProviderArray(anyCarrier).length === 0;
  }

  saveValues(from_btn: boolean = true) {
    let isValid = true;

    this.carriers.controls.forEach((carrierGroup: FormGroup) => {

      const totalPercentage = carrierGroup.value.providers.reduce((sum: number, provider: any) => {
        if (typeof provider.percentageControl != 'number') {
          provider.percentageControl = parseFloat(provider.percentageControl.replace('%', ''));
        }
        return sum + (parseFloat(provider.percentageControl) || 0);
      }, 0);

      if (totalPercentage !== 100) {
        isValid = false;
        this.toastService.info('A soma das porcentagens de cada operadora deve ser igual a 100%');
      }
    });

    if (!isValid) {
      return;
    }

    // Verifica se o formulário está vazio ou se faltam dados obrigatórios
    if (this.form.value.carriers.length == 0) {
      this.toastService.info('Adicione pelo menos uma operadora');
      return;
    }

    // Salvando os dados (com a soma das porcentagens correta)
    const values = this.form.value.carriers.map((carrier: any) => {
      return {
        id: carrier.id,
        carrier_id: carrier.carrierControl.id,
        name: carrier.carrierControl.title,
        fractionation_group_id: this.routeData.id,
        percentageControl: carrier.percentageControl,
        percentage: carrier.percentageControl,
        providers: carrier.providers.map((provider: any) => {
          return {
            providerControl: provider.providerControl.id,
            percentageControl: provider.percentageControl
          };
        })
      };
    });

    // Chama o serviço para salvar
    this.saving = true;
    this.FractionGroupProviderService.addRow(values).subscribe({
      next: () => {
        this.saving = false;
        this.toastService.success('Configuração salva com sucesso');
      },
      error: () => {
        this.saving = false;
        this.toastService.error('Erro ao salvar configuração');
      }
    });
  }

  formatPercent(event) {
    let value = event.target.value;

    // Remover caracteres não numéricos e o símbolo '%'
    value = value.replace(/[^0-9]/g, '');

    event.target.value = value;

    value = value.replace('%', '');

    if (event.data == null) {
      value = value.slice(0, -1);
    }

    if (event.data != null && value[0] == '0') {
      value = value.slice(1);
    }

    if (['', null].includes(value)) {
      value = '0';
    }

    // Atualizar o valor do campo com o valor formatado
    event.target.value = value + '%';
  }

  createConfiguration(saveValues: boolean = false) {
    this.fractionationService
      .addRoute({
        name: this.configurationForm.get('routeName').value.toUpperCase(),
        // Na criação, papéis de sistema só no SMS; no RCS eles se ligam pela lista.
        monitored: !this.isRcs && this.configurationForm.get('monitored').value,
        pix: !this.isRcs && this.configurationForm.get('pix').value,
        default: !this.isRcs && this.configurationForm.get('default').value,
        active: true,
        channel: this.channel
      })
      .subscribe({
        next: (data: FractionationRoute) => {
          this.result?.push(data);

          this.modalService.dismissAll();

          if (saveValues) {
            this.routeData = data;
            this.saveValues();
            this.carriers.controls.forEach((carrier, i) => this.removeCarrierRow(i));
          }

          this.toastService.success('Rota criada com sucesso!');
          this.router.navigate(['/fractionations', data.id]);
        },
        error: (error) => {
          const errorMessage = error.error.toString();
          this.openConfigurationModal();
          this.toastService.error(error.status === 400 ? errorMessage : 'Erro desconhecido ao criar a rota');
        },
      });
  }

  cancelCreation() {
    this.modalService.dismissAll();
    return window.history.back();
  }

  editRoute() {
    this.openConfigurationModal();
  }

  createRoute() {
    if (this.carriers.controls.length < 1) {
      return this.toastService.error('Crie pelo menos uma configuração.');
    }

    let foundAnyCarrierProvider = false;

    this.carriers.controls.map((group: FormGroup) => {
      const control = group.getRawValue().carrierControl
      const providers = group.getRawValue().providers

      if (control.title.toLowerCase() == 'qualquer operadora' && providers.length >= 1) {
        foundAnyCarrierProvider = true;
      }
    });

    if(!foundAnyCarrierProvider) {
      return this.toastService.error('Inclua pelo menos uma opção para "Qualquer operadora" configurada.');
    }

    this.toastService.info('Criando rota... Por favor, aguarde.');

    this.createConfiguration(true);
  }

  updateConfiguration() {
    this.fractionationService.updateRoute(this.routeData.id, this.configurationForm.get('routeName').value).subscribe({
      next: () => {
        this.toastService.success('Rota atualizada com sucesso!');
        this.routeData.name = this.configurationForm.get('routeName').value;
      }
    });
  }

  cancelUpdate() {
    this.modalService.dismissAll();
  }
}
